import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CompanyContext } from 'src/context';
import CustomModal from 'src/components/configuracion/components/customModal';
import ModalActionsFooter from 'src/components/shared/ModalActionsFooter';
import { useActiveBanners } from './ActiveBannersContext';
import { DismissBanner, resolveBannerChannel, TrackBannerLinkClick } from 'src/graphql/banners';
import BannerLink from './BannerLink';
import useSessionBannerDismissals from './useSessionBannerDismissals';
import './AnnouncementModal.scss';

const AnnouncementModal = () => {
  const { selectedCompany } = useContext(CompanyContext) || {};
  const normalizedCompanyId = selectedCompany?.id ? String(selectedCompany.id) : '';
  const { banners } = useActiveBanners();
  const { executeMutation: dismissBanner, fetching: dismissing } = DismissBanner();
  const { executeMutation: trackBannerLinkClick } = TrackBannerLinkClick();
  const { dismiss, filterVisible } = useSessionBannerDismissals(dismissBanner, normalizedCompanyId);
  const [open, setOpen] = useState(false);

  const modalBanners = useMemo(() => {
    const active = banners.filter(
      (banner) => resolveBannerChannel(banner) === 'MODAL',
    );
    return filterVisible(active).sort(
      (a, b) => new Date(a.scheduledAt || 0) - new Date(b.scheduledAt || 0),
    );
  }, [banners, filterVisible]);

  const currentBanner = modalBanners[0] || null;
  const remainingCount = Math.max(modalBanners.length - 1, 0);

  useEffect(() => {
    setOpen(Boolean(currentBanner));
  }, [currentBanner?.id, modalBanners.length]);

  const handleClose = async () => {
    if (!currentBanner?.id) {
      setOpen(false);
      return;
    }
    await dismiss(currentBanner.id);
  };

  if (!currentBanner) return null;

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      size="small"
      classNameModal="announcement-modal"
      classNameContent="announcement-modal__content"
      title={currentBanner.title || 'Comunicado SmartCFO'}
      content={(
        <>
          {modalBanners.length > 1 ? (
            <p className="announcement-modal__queue">
              Comunicado
              {' '}
              1
              {' '}
              de
              {' '}
              {modalBanners.length}
            </p>
          ) : null}
          <p className="announcement-modal__message">
            {currentBanner.message}
            <BannerLink
              linkUrl={currentBanner.linkUrl}
              linkLabel={currentBanner.linkLabel}
              className="announcement-modal__link"
              inline
              onTrackClick={() => {
                if (!currentBanner?.id) return;
                trackBannerLinkClick({
                  bannerId: String(currentBanner.id),
                  companyId: normalizedCompanyId,
                }).catch(() => {});
              }}
            />
          </p>
          {remainingCount > 0 ? (
            <p className="announcement-modal__queue-hint">
              Al cerrar, se mostrará el siguiente comunicado (
              {remainingCount}
              {' '}
              pendiente
              {remainingCount === 1 ? '' : 's'}
              ).
            </p>
          ) : null}
        </>
      )}
      customActions={(
        <ModalActionsFooter
          confirmLabel="Cerrar"
          onConfirm={handleClose}
          confirmLoading={dismissing}
        />
      )}
      closeOnDimmerClick
    />
  );
};

export default AnnouncementModal;
